import {
  buildRestoreFormValues,
  buildRestoreProducts,
} from '@/pages/add-new-order/restoreOrderForm';
import type { BasicProduct, Order } from '@/types/order';
import type { RegionItem } from '@/types/region';
import type { SmartPasteOrder, SmartPasteParse } from '@/types/smartPaste';

/**
 * Bridges a Smart Paste preview into the Add New Order form.
 *
 * `mapped` is already create-order shaped, so the existing back-nav restorer
 * (buildRestoreFormValues / buildRestoreProducts) does the field mapping — the
 * only work left here is the two things it can't know:
 *
 *  1. `province` — the form's area dropdown is driven by a province_code, but
 *     the mapped payload only carries the area's `region_uid`. The parser tells
 *     us which province it resolved, so we look the code up in the province list.
 *  2. `secondary_phone` — core emits it under that name, while Contact (and the
 *     restorer) expect `additional_phones[0]`.
 */

export interface SmartPasteFormFill {
  /** Values for form.setFieldsValue(). Only fields the parser actually found. */
  formValues: Partial<ReturnType<typeof buildRestoreFormValues>>;
  /** Empty when the message named no products — callers must then keep theirs. */
  products: BasicProduct[];
  /** province_code driving the area dropdown, or undefined when unresolved. */
  provinceCode?: string;
}

/**
 * Drop keys the parser had nothing for.
 *
 * On Create the form is empty so this changes nothing, but on Edit it is the
 * difference between filling an order and gutting it: antd's setFieldsValue
 * happily writes `undefined` over a field, so a message that doesn't mention
 * the custom ID would otherwise erase the one already on the order.
 */
function onlyFoundValues<T extends Record<string, unknown>>(values: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(values).filter(([key, v]) => {
      if (v === undefined || v === null || v === '') return false;
      // The shipment flags always come back, `false` by default — that is the
      // parser saying nothing, not the parser saying "not fragile". Carrying
      // the false through would let a message that never mentions cold chain
      // switch it off on an order that needs it. A paste can tick these boxes,
      // never untick them.
      if (SHIPMENT_FLAGS.includes(key)) return v === true;
      return true;
    }),
  ) as Partial<T>;
}

const SHIPMENT_FLAGS = ['isFragile', 'temperatureControl'];

/**
 * Find the province_code for the province the parser resolved. Province rows
 * repeat one row per area, so any row of that province answers it. Matches on
 * the English name first (the parser's canonical form), then the Arabic one.
 */
export function resolveProvinceCodeFromParse(
  parse: SmartPasteParse,
  provinces?: RegionItem[] | null,
): string | undefined {
  if (!provinces?.length) return undefined;

  const en = parse.location?.province_en?.trim().toLowerCase();
  const ar = parse.location?.province_ar?.trim();

  const match = provinces.find((row) => {
    const rowEn = row.province_en_name?.trim().toLowerCase();
    const rowAr = row.province?.trim();
    return (!!en && rowEn === en) || (!!ar && rowAr === ar);
  });

  return match?.province_code || undefined;
}

/**
 * Turn a non-blocked Smart Paste order into everything the form needs.
 * Returns null for a blocked order — there is no mapped payload to apply.
 */
export function buildSmartPasteFormFill(
  order: SmartPasteOrder,
  provinces?: RegionItem[] | null,
): SmartPasteFormFill | null {
  if (order.blocked || !order.mapped) return null;

  // Core sends the second number as `secondary_phone`; the restorer reads
  // `additional_phones[0]`. Normalize before handing it over.
  const secondary = (order.mapped as Order & { contact?: { secondary_phone?: string } }).contact
    ?.secondary_phone;

  const payload: Order = {
    ...order.mapped,
    contact: order.mapped.contact
      ? {
          ...order.mapped.contact,
          additional_phones:
            order.mapped.contact.additional_phones ?? (secondary ? [secondary] : undefined),
        }
      : order.mapped.contact,
  };

  const provinceCode = resolveProvinceCodeFromParse(order.parse, provinces);

  return {
    formValues: onlyFoundValues(buildRestoreFormValues(payload, provinceCode)),
    // Quantity is deliberately NOT taken from the message. "٥ قطع" is far more
    // often the customer describing the goods than a line-item count, and the
    // order total is price x quantity — so believing it silently multiplies
    // what the driver collects. Always 1; the merchant changes it if they meant
    // it. Fixed here rather than in buildRestoreProducts, which also restores
    // real drafts where a typed quantity must survive.
    products: buildRestoreProducts(payload.products).map((product) => ({
      ...product,
      quantity: '1',
    })),
    provinceCode,
  };
}
